// src/layout/Layout.js
import '../custom.css'

import React, { useEffect, useState } from 'react'
import {
  Navbar,
  Container,
  Nav,
  Image,
  Button,
  Row,
  Col,
  Form,
  InputGroup,
} from 'react-bootstrap'
import {
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Tiktok,
  Youtube,
  Pinterest,
} from 'react-bootstrap-icons'
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom'

import Seo from '../components/Seo'
import { getSeoMeta } from '../data/seoMeta'

const logoUrl    = '/assets/logo/greatly-social-full.png'
const appStore   = 'https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg'
const googlePlay = 'https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg'

export default function Layout () {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [isLoggedIn, setIsLoggedIn] = useState(false)

  useEffect(() => {
    setIsLoggedIn(!!localStorage.getItem('token'))
  }, [])

  const handleLogout = () => {
    localStorage.removeItem('token')
    setIsLoggedIn(false)
    navigate('/')
  }

  /* ───────── HEADER ───────── */
  const Header = () => (
    <Navbar bg='white' expand='lg' sticky='top' className='border-bottom py-1'>
      <Container>
        <Navbar.Brand as={Link} to='/' className='d-flex align-items-center'>
          <Image src={logoUrl} alt='Greatly Social logo' height={50} className='me-2' />
        </Navbar.Brand>

        <Navbar.Toggle aria-controls='main-nav' />
        <Navbar.Collapse id='main-nav' className='justify-content-end'>
          <Nav className='me-lg-4 gap-lg-3'>
            <Nav.Link as={Link} to='/'      className='fw-semibold text-dark'>Features</Nav.Link>
            <Nav.Link as={Link} to='/plans' className='fw-semibold text-dark'>Pricing</Nav.Link>
            <Nav.Link as={Link} to='/blog'  className='fw-semibold text-dark'>Blog</Nav.Link>
          </Nav>

          {isLoggedIn ? (
            <Button variant='outline-primary' size='sm' onClick={handleLogout}>
              Logout
            </Button>
          ) : (
            <div className='d-flex gap-2'>
              <Button as={Link} to='/login'    variant='outline-primary' size='sm'>Log&nbsp;in</Button>
              <Button as={Link} to='/register' variant='primary' size='sm'>Get&nbsp;started</Button>
            </div>
          )}
        </Navbar.Collapse>
      </Container>
    </Navbar>
  )

  /* ───────── FOOTER ───────── */
  const Footer = () => (
    <footer className='bg-dark text-light pt-5'>
      <Container>
        {/* —— top grid —— */}
        <Row className='gy-5'>
          {/* Features */}
          <Col md={3} lg={2}>
            <h6 className='footer-heading mb-3'>Features</h6>
            {[
              ['Post Scheduling',     '/post-scheduling'],
              ['AI Post Creation',    '/ai-post-creation'],
              ['Bulk Posting',        '/bulk-posting'],
              ['Video Editor',        '/video-photo-editor'],
              ['Content Calendar',    '/calendar-view'],
              ['Link in Bio',         '/link-in-bio'],
            ].map(([label, path]) => (
              <Nav.Link key={path} as={Link} to={path} className='p-0 footer-link mb-1'>
                {label}
              </Nav.Link>
            ))}
          </Col>

          {/* Schedule by platform — added 2026-08-14. These pages had zero
              inbound links; the content map surfaced them as orphans. */}
          <Col md={3} lg={2}>
            <h6 className='footer-heading mb-3'>By platform</h6>
            {[
              ['Instagram Scheduler', '/instagram-scheduler'],
              ['TikTok Scheduler',    '/tiktok-scheduler'],
              ['LinkedIn Scheduler',  '/linkedin-scheduler'],
            ].map(([label, path]) => (
              <Nav.Link key={path} as={Link} to={path} className='p-0 footer-link mb-1'>
                {label}
              </Nav.Link>
            ))}
            <h6 className='footer-heading mb-3 mt-4'>
              <Link to='/tools' className='text-reset text-decoration-none'>Free tools</Link>
            </h6>
            {[
              ['LinkedIn Text Formatter', '/tools/linkedin-text-formatter'],
              ['Emoji Translator', '/tools/emoji-translator'],
              ['Instagram Grid Planner', '/tools/instagram-grid-planner'],
              ['Instagram Name Generator', '/tools/instagram-name-generator'],
              ['TikTok Username Generator', '/tools/tiktok-username-generator'],
              ['YouTube Name Generator', '/tools/youtube-name-generator'],
              ['Aesthetic Username Generator', '/tools/aesthetic-username-generator'],
              ['Podcast Name Generator', '/tools/podcast-name-generator'],
              ['UTM Builder',       '/tools/utm-builder'],
              ['Best Time to Post', '/best-time-to-post'],
              ['Engagement Rate Calculator', '/tools/engagement-rate-calculator'],
              ['Instagram Bio Generator', '/tools/instagram-bio-generator'],
              ['Instagram Caption Generator', '/tools/instagram-caption-generator'],
              ['YouTube Description Generator', '/tools/youtube-description-generator'],
              ['Instagram Post Size + Resizer', '/tools/instagram-post-size'],
              ['Content Calendar Template', '/tools/content-calendar-template'],
              ['LinkedIn Banner Size', '/tools/linkedin-banner-size'],
              ['YouTube Banner Size', '/tools/youtube-banner-size'],
              ['Facebook Event Cover Size', '/tools/facebook-event-cover-photo-size'],
              ['Social Media Report Template', '/tools/social-media-report-template'],
            ].map(([label, path]) => (
              <Nav.Link key={path} as={Link} to={path} className='p-0 footer-link mb-1'>
                {label}
              </Nav.Link>
            ))}
          </Col>

          {/* For your business */}
          <Col md={3} lg={2}>
            <h6 className='footer-heading mb-3'><Link to='/for' className='text-reset text-decoration-none'>For your business</Link></h6>
            {[
              ['Small Business', '/for/small-business'],
              ['Agencies',       '/for/agencies'],
              ['Real Estate',    '/for/real-estate'],
              ['Salons & Barbers','/for/salons'],
              ['Dentists',        '/for/dentists'],
              ['Restaurants',     '/for/restaurants'],
              ['Nonprofits',      '/for/nonprofits'],
              ['Photographers',   '/for/photographers'],
              ['Churches',        '/for/churches'],
              ['Gyms & Fitness',  '/for/gyms'],
              ['Law Firms',       '/for/law-firms'],
              ['Schools',         '/for/schools'],
              ['Musicians',       '/for/musicians'],
              ['Consultants',     '/for/consultants'],
              ['Insurance Agents','/for/insurance-agents'],
              ['Financial Advisors','/for/financial-advisors'],
              ['Chiropractors',   '/for/chiropractors'],
              ['Med Spas',        '/for/med-spas'],
              ['Car Dealerships', '/for/car-dealerships'],
              ['Accountants',     '/for/accountants'],
              ['Contractors',     '/for/contractors'],
              ['Plumbers & HVAC', '/for/plumbers'],
              ['Startups & SaaS', '/for/startups'],
              ['Authors',         '/for/authors'],
              ['Bakeries',        '/for/bakeries'],
            ].map(([label, path]) => (
              <Nav.Link key={path} as={Link} to={path} className='p-0 footer-link mb-1'>
                {label}
              </Nav.Link>
            ))}
          </Col>

          {/* Company (now includes legal) */}
          <Col md={3} lg={2}>
            <h6 className='footer-heading mb-3'>Company</h6>
            {[
              ['About',            '/about'],
              ['Careers',          '/careers'],
              ['Press',            '/press'],
              ['Affiliate program','/affiliate-program'],
              ['Privacy Policy',   '/privacy'],
              ['Terms of Service', '/terms'],
            ].map(([label, path]) => (
              <Nav.Link key={path} as={Link} to={path} className='p-0 footer-link mb-1'>
                {label}
              </Nav.Link>
            ))}
          </Col>

          {/* Get started */}
          <Col md={3} lg={2}>
            <h6 className='footer-heading mb-3'>Get started</h6>
            {[
              ['Sign up for Free', '/register'],
              ['Plans & Pricing',  '/plans'],
              ['Contact Us',       '/contact'],
              ['Blog',             '/blog'],
              ['Support',          '/p/support'],
            ].map(([label, path]) => (
              <Nav.Link key={path} as={Link} to={path} className='p-0 footer-link mb-1'>
                {label}
              </Nav.Link>
            ))}
          </Col>

          {/* Newsletter */}
          <Col md={12} lg={2}>
            <h6 className='footer-heading mb-3'>Join our newsletter</h6>
            <p className='small text-light'>Stay updated with the latest news and tips</p>

            <InputGroup className='mb-3'>
              <Form.Control
                type='email'
                placeholder='Enter your email'
                className='rounded-pill border-0'
              />
              <Button variant='primary' className='rounded-pill ms-2 px-4'>Subscribe</Button>
            </InputGroup>

            <Form.Check
              type='checkbox'
              id='newsletterConsent'
              label={<span className='small text-light'>
                I agree to receive marketing communication. I can unsubscribe at any time.
              </span>}
              className='text-start'
            />
          </Col>
        </Row>

        <hr className='border-secondary opacity-25 my-4' />

        {/* —— follow / stores —— */}
        <Row className='align-items-center gy-4'>
          <Col lg={4}>
            <div className='d-flex gap-3 justify-content-lg-start justify-content-center'>
              {[Instagram, Tiktok, Linkedin, Facebook, Twitter, Youtube, Pinterest].map((Icon, i) => (
                <a key={i} href='#' className='text-light'>
                  <Icon size={22} />
                </a>
              ))}
            </div>
          </Col>

          <Col lg={4} className='text-center'>
            <Image src={logoUrl} alt='Greatly Social' height={40} />
          </Col>

          <Col lg={4} className='d-flex gap-3 justify-content-lg-end justify-content-center'>
            {/* UPDATED LINKS BELOW */}
            <a href='https://apps.apple.com/us/app/greatly-social/id1563430092' target="_blank" rel="noopener noreferrer">
              <Image src={appStore} alt='Download on the App Store' height={40} />
            </a>
            <a href='https://play.google.com/store/apps/details?id=com.app.greatlysocial' target="_blank" rel="noopener noreferrer">
              <Image src={googlePlay} alt='Get it on Google Play' height={40} />
            </a>
          </Col>
        </Row>

        <hr className='border-secondary opacity-25 my-4' />

        {/* —— legal line (links removed) —— */}
        <Row>
          <Col className='small text-muted text-center'>
            © {new Date().getFullYear()} Greatly Social
          </Col>
        </Row>
      </Container>
    </footer>
  )

  return (
    <>
      {/* Default <head> for every public route, driven by src/data/seoMeta.js.
          Pages with dynamic content (e.g. BlogPost) render their own <Seo>,
          which overrides this one. */}
      <Seo {...getSeoMeta(pathname)} />

      <Header />

      {/* hero flush with navbar */}
      <Container fluid className='p-0'>
        <Outlet />
      </Container>

      <Footer />
    </>
  )
}
